import { createFileRoute } from "@tanstack/react-router";
import { Bell, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getApiWaitingOrErrorUI } from "@/components/generic/errors";
import { SinglePropertyCard } from "@/components/generic/single-property-card";
import { AlarmsCard } from "@/components/status/alarms-card";
import { NotificationsCard } from "@/components/status/notifications-card";
import { RealTimeDataWaitingAlert } from "@/components/status/real-time-data-waiting-alert";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";

import { type ApiError, isApiWaitingOrError } from "@/data-interfaces/generics";
import type { RealTimeData } from "@/data-interfaces/real-time-data";
import { useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getStatusProperties } from "@/lib/data-remappings/status";
import { getAppWrappedPageUrl, STATUS_PAGE_URL } from "@/lib/page-routes";
import { getRtdSubscriptionTopic } from "@/lib/request-paths";
import { getMachineSerialNumber } from "@/lib/session-storage-keys";

export const Route = createFileRoute(getAppWrappedPageUrl(STATUS_PAGE_URL))({
	component: Status,
});

function Status() {
	const [t, _] = useTranslation();
	const [te, __] = useTranslation("apiErrors");

	const [realTimeData, setRealTimeData] = useState<
		RealTimeData | ApiError | null
	>(null);

	useMQTTSubscribe(
		getRtdSubscriptionTopic(getMachineSerialNumber()),
		setRealTimeData,
		true,
	);

	return (
		<div className="text-white p-8 flex flex-col flex-1">
			{isApiWaitingOrError(realTimeData) ? (
				<div className="flex flex-col gap-y-4">
					<h1 className="text-3xl sm:text-4xl font-medium">{t("Status")}</h1>

					{getApiWaitingOrErrorUI(
						realTimeData,
						"Error retrieving real-time data",
						te,
					)}
				</div>
			) : (
				<>
					<div className="flex flex-row justify-between">
						<h1 className="text-3xl sm:text-4xl font-medium">{t("Status")}</h1>
						<div className="flex flex-row gap-x-3 min-[1850px]:hidden">
							<Dialog>
								<DialogTrigger className="text-red-500">
									<TriangleAlert />
								</DialogTrigger>
								<DialogContent className="p-0 bg-transparent border-transparent">
									<DialogHeader className="hidden">
										<DialogTitle>{t("Alarms")}</DialogTitle>
										<DialogDescription />
									</DialogHeader>
									<AlarmsCard
										alarms={realTimeData.alarms}
										className="bg-red-500"
									/>
								</DialogContent>
							</Dialog>
							<Dialog>
								<DialogTrigger>
									<Bell />
								</DialogTrigger>
								<DialogContent className="p-0 bg-transparent border-transparent">
									<DialogHeader className="hidden">
										<DialogTitle>{t("Notifications")}</DialogTitle>
										<DialogDescription />
									</DialogHeader>
									<NotificationsCard
										notifications={realTimeData.last_notifications}
									/>
								</DialogContent>
							</Dialog>
						</div>
					</div>

					<div className="grid grid-cols-1 gap-4 pt-4 h-full min-[1240px]:grid-cols-2 min-[1240px]:grid-rows-2 min-[1850px]:grid-cols-3 min-[1850px]:grid-rows-3">
						<div className="grid grid-cols-subgrid min-[1240px]:row-span-2 min-[1240px]:col-span-2 gap-4">
							{getStatusProperties(realTimeData).map((prop) => {
								return (
									<SinglePropertyCard
										key={prop.title}
										className="max-w-md max-h-42"
										singlePropertyObject={prop}
									/>
								);
							})}
						</div>

						<NotificationsCard
							notifications={realTimeData.last_notifications}
							className="row-span-3 hidden min-[1850px]:flex"
						/>

						<AlarmsCard
							alarms={realTimeData.alarms}
							className="bg-red-500 col-span-2 hidden min-[1850px]:flex"
						/>
					</div>
				</>
			)}

			<RealTimeDataWaitingAlert dataToWait={realTimeData} />
		</div>
	);
}
